class TabCollectionCarousel extends HTMLElement {
  constructor() {
    super();
    this.tabs = this.querySelectorAll('.tab-collection-carousel-tab');
    this.panels = this.querySelectorAll('.tab-collection-carousel-panel');
    // Map<panelElement, SwiperInstance> - only holds panels that have been inited
    this.swipers = new Map();
    this._onTabClick = this._onTabClick.bind(this);
  }
 
  connectedCallback() {
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this._onTabClick);
    });
 
    // Init the swiper for whichever panel is active by default (usually the first).
    const activePanel = this.querySelector('.tab-collection-carousel-panel.is-active');
    if (activePanel) this.initSwiperForPanel(activePanel);
  }
 
  disconnectedCallback() {
    // Clean up listeners and destroy any Swiper instances so the theme
    // editor's "section:load" re-render doesn't leave orphaned instances
    // or throw "Swiper already initialized" on the same DOM node.
    this.tabs.forEach((tab) => tab.removeEventListener('click', this._onTabClick));
 
    this.swipers.forEach((swiper) => {
      if (swiper && typeof swiper.destroy === 'function') {
        swiper.destroy(true, true);
      }
    });
    this.swipers.clear();
  }
 
  _onTabClick(event) {
    this.activateTab(event.currentTarget);
  }
 
  activateTab(tab) {
    const targetId = tab.getAttribute('data-tab-target');
 
    this.tabs.forEach((t) => {
      t.classList.remove('is-active');
      t.setAttribute('aria-selected', 'false');
    });
    tab.classList.add('is-active');
    tab.setAttribute('aria-selected', 'true');
 
    this.panels.forEach((panel) => {
      if (panel.id === targetId) {
        panel.classList.add('is-active');
        panel.removeAttribute('hidden');
        this.initSwiperForPanel(panel);
      } else {
        panel.classList.remove('is-active');
        panel.setAttribute('hidden', '');
      }
    });
  }
 
  initSwiperForPanel(panel) {
    // Already inited: just recalculate, since the panel may have been
    // resized/hidden while inactive (widths, new slide count, etc.).
    if (this.swipers.has(panel)) {
      const existing = this.swipers.get(panel);
      // update() must run after the panel is visible, so defer one frame.
      requestAnimationFrame(() => existing.update());
      return;
    }
 
    const sliderEl = panel.querySelector('.swiper');
    if (!sliderEl || !window.Swiper) return;
 
    // Panel must be visible (not [hidden]) before Swiper measures it,
    // otherwise slide widths are calculated as 0.
    requestAnimationFrame(() => {
      const swiper = new window.Swiper(sliderEl, {
        slidesPerView: 2.5,
        centeredSlides: false,
        spaceBetween: 16,
        loop: false,
        autoHeight: false,
        // String selectors are automatically scoped to this swiper's own
        // container (sliderEl), so reusing the same classes across every
        // tab panel is safe - each instance only looks at its own children.
        pagination: {
          el: '.swiper-pagination',
          clickable: true,
        },
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
        breakpoints: {
          744: {
            slidesPerView: 3.5,
            slidesPerGroup: 2,
          },
          1280: {
            spaceBetween: 24,
            slidesPerView: 4.5,
            slidesPerGroup: 1,
          },
        },
      });
 
      this.swipers.set(panel, swiper);
    });
  }
}
 
if (!customElements.get('tab-collection-carousel')) {
  customElements.define('tab-collection-carousel', TabCollectionCarousel);
}